import { createSelector } from 'reselect';
import { DeviceType } from 'enums';
import bootstrapSelector from 'common/selectors/bootstrapSelector';
import appSelector, {
    apiEnvSelector,
    apiVersionSelector,
    editionSelector,
    isDomesticSelector
} from 'common/selectors/appSelector';
import {
    zipcodeSelector,
    authStatesSelector,
    preauthorizedResourcesSelector
} from 'common/selectors/authenticationSelectors';
import { oneIdEntitlementsParamsSelector } from 'common/selectors/oneIdSelector';
import { favoriteTeamSelector, favoriteSportSelector } from 'common/selectors/fanSelector';
import { dateFormat } from 'lib/format';

const apiConfigParams = ['apiEnv', 'apiVersion'];

const apiParamsSelector = createSelector(
    appSelector,
    apiEnvSelector,
    apiVersionSelector,
    (app, apiEnv, apiVersion) => {
        const { device: deviceType, country, lang, sourceLang, packageId, tier, edition } = app;
        const tz = dateFormat.getTimezoneParam();
        return {
            apiEnv,
            apiVersion,
            countryCode: country,
            deviceType,
            lang,
            sourceLang,
            tz,
            packageId,
            tier,
            edition
        };
    }
);

export const shieldApiKeySelector = createSelector(
    appSelector,
    editionSelector,
    (
        { bootstrap: { shieldApiKey: shieldApiKeys } },
        { edition }
    ) => {
        if (shieldApiKeys[edition]) {
            return shieldApiKeys[edition];
        }

        // If we haven't returned, go with domestic/US.
        return shieldApiKeys.domestic;
    }
);

export const graphqlParamsSelector = createSelector(
    appSelector,
    apiEnvSelector,
    (app, apiEnv) => {
        const { device, country: countryCode, lang, packageId } = app;
        const tz = dateFormat.getTimezoneParam();
        return {
            apiEnv,
            countryCode,
            deviceType: DeviceType[device],
            lang,
            tz,
            packageId
        };
    }
);

export const collectionParamsSelector = createSelector(
    appSelector,
    (app) => {
        const { country, lang } = app;
        return {
            countryCode: country,
            lang
        };
    }
);

export function getWhitelistedParams(paramsArg, queryParameters) {
    let params = paramsArg;
    if (queryParameters) {
        const whitelisted = queryParameters.concat(apiConfigParams);
        params = Object.keys(params).reduce((acc, key) => {
            if (whitelisted.indexOf(key) > -1) {
                acc[key] = params[key];
            }
            return acc;
        }, {});
    }

    return params;
}

export const searchApiParamsSelector = createSelector(
    bootstrapSelector,
    isDomesticSelector,
    apiParamsSelector,
    preauthorizedResourcesSelector,
    zipcodeSelector,
    (
        { pageApi: { queryParameters } = {} },
        isDomestic,
        apiParams,
        authNetworks,
        zipcode
    ) => {
        let params = isDomestic ? {
            ...apiParams,
            authNetworks,
            zipcode
        } : apiParams;

        params = getWhitelistedParams(params, queryParameters);

        return params;
    }
);

export const suggestApiParamsSelector = createSelector(
    bootstrapSelector,
    isDomesticSelector,
    apiParamsSelector,
    preauthorizedResourcesSelector,
    zipcodeSelector,
    (
        { pageApi: { queryParameters } = {} },
        isDomestic,
        apiParams,
        authNetworks,
        zipcode
    ) => {
        let params = isDomestic ? {
            ...apiParams,
            authNetworks,
            zipcode
        } : apiParams;

        params = getWhitelistedParams(params, queryParameters);

        return params;
    }
);

export const pageApiFeatureFlagsSelector = createSelector(
    bootstrapSelector,
    (
        { pageApi: { featureFlags = [] } = {} },
    ) => {
        const features = featureFlags.length ? featureFlags.join(',') : undefined;

        return {
            features
        };
    }
);

export const pageAPIParamsSelector = createSelector(
    bootstrapSelector,
    isDomesticSelector,
    apiParamsSelector,
    authStatesSelector,
    preauthorizedResourcesSelector,
    oneIdEntitlementsParamsSelector,
    zipcodeSelector,
    favoriteTeamSelector,
    favoriteSportSelector,
    pageApiFeatureFlagsSelector,
    (
        { pageApi: { queryParameters } = {} },
        isDomestic,
        apiParams,
        authStates,
        authNetworks,
        entitlements,
        zipcode,
        favoriteTeam,
        favoriteSport,
        features,
    ) => {
        let params = isDomestic ? {
            ...apiParams,
            ...features,
            authStates,
            authNetworks,
            entitlements,
            zipcode,
            favoriteTeam,
            favoriteSport
        } : {
            ...apiParams,
            ...features
        };

        params = getWhitelistedParams(params, queryParameters);

        return params;
    }
);

// `/web/event` and `/web/video`
export const pageAPILimitedParamsSelector = createSelector(
    bootstrapSelector,
    isDomesticSelector,
    apiParamsSelector,
    zipcodeSelector,
    oneIdEntitlementsParamsSelector,
    pageApiFeatureFlagsSelector,
    (
        { pageApi: { queryParameters } = {} },
        isDomestic,
        apiParams,
        zipcode,
        entitlements,
        features
    ) => {
        let params = isDomestic ? {
            ...apiParams,
            ...features,
            entitlements,
            zipcode
        } : {
            ...apiParams,
            ...features
        };

        params = getWhitelistedParams(params, queryParameters);

        return params;
    }
);

export default apiParamsSelector;



// WEBPACK FOOTER //
// ./app/common/selectors/apiParamsSelector.js